/* global SingleSignOnEnvironment */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import { strikeBannerEnabled } from 'helpers/ConfigHelper';
import { CCPA_OPT_OUT_COOKIE_NAME } from 'helpers/ContentHelper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import { getCookie } from 'helpers/Storage';
import Style from './StrikeBanner.module.scss';

export const STRIKE_PAGE_TYPE_HOME = 'homepage';
export const STRIKE_PAGE_TYPE_SECTION_FRONT = 'section-front';
export const STRIKE_PAGE_TYPE_SCHEDULE = 'schedule';

const StrikeBanner = ({ index, pageType, breakpoint = null }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const scriptId = 'script-strikebanner';
  const uspString = getCookie({ name: CCPA_OPT_OUT_COOKIE_NAME });

  const styles = {
    [SITE_PLATFORM_MOBILE]: `mobile-ad ${Style.mobile}`,
    [SITE_PLATFORM_TABLET]: `tablet-ad ${Style.tablet}`,
    [SITE_PLATFORM_WEB]: `desktop-ad ${Style.desktop}`,
    [SITE_PLATFORM_WEB_LARGE]: `desktop-ad ${Style.desktop}`,
  };

  const ids = {
    [SITE_PLATFORM_MOBILE]: `mw-ad-ban${index}`,
    [SITE_PLATFORM_TABLET]: `tw-ad-lb${index}`,
    [SITE_PLATFORM_WEB]: `desk-ad-lb${index}`,
    [SITE_PLATFORM_WEB_LARGE]: `desk-ad-lb${index}`,
  };

  const afterLoad = () => {
    window.foxstrike = window.foxstrike || {};
    window.foxstrike.cmd = window.foxstrike.cmd || [];
    window.foxstrike.config = window.foxstrike.config || {};
    window.foxstrike.config.meta = window.foxstrike.config.meta || {};

    window.foxstrike.cmd.push(Strike => {
      Strike.pageReady();
    });
    setIsLoaded(true);
  };

  const addScript = useCallback(() => {
    if (document.getElementById(scriptId)) {
      afterLoad();
      return;
    }
    const prefixes = { production: '', stage: '', qa: 'dev-', dev: 'dev-' };
    const env = prefixes[SingleSignOnEnvironment];
    const scriptUrl = `https://${env}strike.fox.com/static/ent/display/loader.js`;

    const scriptTag = document.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.id = scriptId;
    scriptTag.src = scriptUrl;
    scriptTag.onload = afterLoad;
    scriptTag.onerror = () => {};
    document.head.appendChild(scriptTag);
  }, []);

  const finalize = () => {
    if (isLoaded) {
      window.foxstrike.cmd.push(Strike => {
        Strike.destroy();
      });
    }
  };

  useEffect(() => {
    addScript();
    return finalize;
  }, [index, addScript]);

  if (isLoaded && !isInitialized) {
    setIsInitialized(true);

    window.foxstrike.cmd.push(Strike => {
      Strike.setSection(pageType);
      Strike.setUSPValue(uspString);
    });

    window.foxstrike.cmd.push(Strike => {
      Strike.insertAd('mobile', `ban${index}`, ids[SITE_PLATFORM_MOBILE], pageType);
    });
    window.foxstrike.cmd.push(Strike => {
      Strike.insertAd('tablet', `lb${index}`, ids[SITE_PLATFORM_TABLET], pageType);
    });
    window.foxstrike.cmd.push(Strike => {
      Strike.insertAd('desktop', `lb${index}`, ids[SITE_PLATFORM_WEB], pageType);
    });
  }
  if (!strikeBannerEnabled()) return null;

  if (breakpoint) {
    return (
      <div className={`ad-container ${Style.container}`}>
        <div className={styles[breakpoint]} id={ids[breakpoint]} />
      </div>
    );
  }

  return (
    <div className={`ad-container ${Style.container}`}>
      <div className={styles[SITE_PLATFORM_WEB]} id={ids[SITE_PLATFORM_WEB]} />
      <div className={styles[SITE_PLATFORM_TABLET]} id={ids[SITE_PLATFORM_TABLET]} />
      <div className={styles[SITE_PLATFORM_MOBILE]} id={ids[SITE_PLATFORM_MOBILE]} />
    </div>
  );
};

StrikeBanner.propTypes = {
  index: PropTypes.number,
  pageType: PropTypes.oneOf([
    STRIKE_PAGE_TYPE_HOME,
    STRIKE_PAGE_TYPE_SECTION_FRONT,
    STRIKE_PAGE_TYPE_SCHEDULE,
  ]),
  breakpoint: PropTypes.string,
};
export default StrikeBanner;
